import { RubyGemMark } from '../components/RubyGemMark';
/**
 * APP-ISSUE DRAWER — the issue detail, rendered as a slide-over ON TOP of the
 * queue (SuperAdminAppIssuesQueue keeps the list mounted underneath). This
 * replaced the old standalone /app-issues/:id page: opening is instant, and
 * closing (X, backdrop, Esc, or browser back) lands exactly where you were in
 * the list. Deep links still work — the queue route matches /app-issues/* and
 * mounts this drawer for the id.
 */
import { RubyLoader } from '../components/RubyLoader';
import React, { useEffect, useRef, useState } from 'react';
import {
  X, Copy, CheckCircle, Send, AlertCircle, Trash2,
} from 'lucide-react';
import { issuesApi } from '../services/issuesApi';
import { toastService } from '../services/toastService';
import { RUBY } from '../components/staff/StaffChrome';

export const ISSUE_STATUSES: Array<{ v: 'new' | 'in_progress' | 'deploying' | 'awaiting_retest' | 'resolved' | 'rejected'; l: string }> = [
  { v: 'new', l: 'New' },
  { v: 'in_progress', l: 'In Progress' },
  { v: 'deploying', l: 'Deploying' },
  { v: 'awaiting_retest', l: 'Awaiting Retest' },
  { v: 'resolved', l: 'Resolved' },
  { v: 'rejected', l: 'Rejected' },
];

const fmtTs = (ts: number) => new Date(ts).toLocaleString();

const AppIssueDrawer: React.FC<{
  id: string;
  onClose: () => void;
  /** Called with the updated issue after a status change, so the list row updates in place. */
  onIssueUpdated?: (issue: any) => void;
  /** Called after a successful delete, so the list removes the row. */
  onIssueDeleted?: (id: string) => void;
}> = ({ id, onClose, onIssueUpdated, onIssueDeleted }) => {
  const [issue, setIssue] = useState<any | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');
  const [commentText, setCommentText] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [aiPrompt, setAiPrompt] = useState<{ prompt: string; screenshotUrl?: string } | null>(null);
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [copied, setCopied] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const aiPromptRef = useRef<HTMLDivElement | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await issuesApi.get(id);
      setIssue(res.issue);
      setComments(res.comments || []);
      if (res.issue?.screenshotKey) {
        issuesApi
          .getScreenshotUrl(id)
          .then((s) => setScreenshotUrl(s.url))
          .catch(() => setScreenshotUrl(null));
      } else {
        setScreenshotUrl(null);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load issue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Esc closes the drawer (unless the delete confirm is up — Esc closes that first).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (showDeleteConfirm) setShowDeleteConfirm(false);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, showDeleteConfirm]);

  const changeStatus = async (status: 'new' | 'in_progress' | 'deploying' | 'awaiting_retest' | 'resolved' | 'rejected') => {
    setSavingStatus(true);
    try {
      const res = await issuesApi.update(id, { status, resolutionNote: resolutionNote || undefined });
      setIssue(res.issue);
      setResolutionNote('');
      onIssueUpdated?.(res.issue);
      load();
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to update status');
    } finally {
      setSavingStatus(false);
    }
  };

  const postComment = async () => {
    if (!commentText.trim()) return;
    setPostingComment(true);
    try {
      await issuesApi.comment(id, commentText.trim());
      setCommentText('');
      await load();
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to post comment');
    } finally {
      setPostingComment(false);
    }
  };

  const loadAiPrompt = async () => {
    setLoadingPrompt(true);
    try {
      const res = await issuesApi.getAiPrompt(id);
      setAiPrompt({ prompt: res.prompt, screenshotUrl: res.screenshotUrl });
      // Copy immediately too — one click gets it onto the clipboard.
      try {
        await navigator.clipboard.writeText(res.prompt);
        toastService.success('AI prompt copied to clipboard');
      } catch { /* clipboard blocked — the Copy button below still works */ }
      requestAnimationFrame(() => {
        aiPromptRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to load AI prompt');
    } finally {
      setLoadingPrompt(false);
    }
  };

  const copyPrompt = async () => {
    if (!aiPrompt?.prompt) return;
    try {
      await navigator.clipboard.writeText(aiPrompt.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error(e);
    }
  };

  const deleteThisIssue = async () => {
    setDeleting(true);
    try {
      await issuesApi.delete(id);
      onIssueDeleted?.(id);
      onClose();
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to delete issue');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const ctx = issue?.context || {};

  return (
    <div className="fixed inset-0 z-[9000]">
      {/* Backdrop — click to close, list stays visible behind it */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Panel */}
      <div className="absolute right-0 top-0 h-full w-full max-w-3xl bg-stone-100 shadow-2xl flex flex-col">
        {/* Black header — same chrome language as the page header */}
        <div className="bg-black shrink-0">
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="text-base font-semibold text-white flex items-center gap-2 truncate">
                <span className="font-mono text-gray-500 text-sm">#{id.slice(-8)}</span>
                <span className="truncate">{issue?.title || (loading ? 'Loading…' : 'Issue')}</span>
              </div>
              {issue && (
                <div className="text-[11px] text-gray-400 truncate">
                  Reported {fmtTs(issue.createdAt)} by {issue.reporterName || issue.reporterEmail}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={loadAiPrompt}
                disabled={loadingPrompt || loading}
                className="px-3 py-1.5 text-sm rounded-lg text-white flex items-center gap-1.5 disabled:opacity-50"
                style={{ background: RUBY }}
              >
                {loadingPrompt ? <RubyGemMark size={14} drawing color="currentColor" /> : <Send size={14} />}
                AI prompt
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={loading}
                className="px-3 py-1.5 text-sm rounded-lg border border-red-500/50 text-red-300 hover:bg-red-900/30 flex items-center gap-1.5 disabled:opacity-50"
                title="Delete this ticket"
              >
                <Trash2 size={14} />
              </button>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-lg hover:bg-white/10 flex items-center justify-center text-white"
                title="Close (Esc)"
              >
                <X size={18} />
              </button>
            </div>
          </div>
          <div style={{ background: `linear-gradient(90deg, #e8365a, ${RUBY}, #9F1239)`, height: '3px' }} />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="relative">
              <div className="space-y-3 animate-pulse">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="bg-white border border-stone-200 rounded-xl p-4">
                    <div className="h-3 w-32 bg-stone-200 rounded mb-3" />
                    <div className="h-3 w-full bg-stone-100 rounded mb-2" />
                    <div className="h-3 w-2/3 bg-stone-100 rounded" />
                  </div>
                ))}
              </div>
              <RubyLoader variant="overlay" size={44} />
            </div>
          ) : error || !issue ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
              <AlertCircle size={14} className="inline mr-1" />{error || 'Not found'}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Status — right at the top, no scrolling needed */}
              <div className="bg-white border border-stone-200 rounded-xl shadow-sm p-4">
                <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Status</div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {ISSUE_STATUSES.map((s) => (
                    <button
                      key={s.v}
                      onClick={() => changeStatus(s.v)}
                      disabled={savingStatus || s.v === issue.status}
                      className={`px-3 py-1.5 text-xs rounded-lg border ${
                        s.v === issue.status
                          ? 'border-transparent text-white'
                          : 'bg-white border-stone-300 text-stone-700 hover:bg-stone-50'
                      } disabled:opacity-60`}
                      style={s.v === issue.status ? { background: RUBY } : undefined}
                    >
                      {s.l}
                    </button>
                  ))}
                  {savingStatus && <span className="inline-flex self-center"><RubyGemMark size={14} drawing /></span>}
                </div>
                <input
                  type="text"
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  placeholder="Resolution note (optional)…"
                  className="w-full bg-white border border-stone-300 rounded-lg px-3 py-2 text-sm text-stone-900 placeholder-stone-400 focus:border-admin-primary focus:outline-none"
                />
                <div className="text-[11px] text-stone-500 mt-1">
                  Add a note that will be shown to the reporter when you mark Awaiting Retest.
                </div>
              </div>

              <div className="bg-white border border-stone-200 rounded-xl shadow-sm p-4">
                <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">User's description</div>
                <p className="text-sm text-stone-800 whitespace-pre-wrap">{issue.description || '_(no description)_'}</p>
              </div>

              {ctx.route && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">Reproduce</div>
                  <div className="text-sm font-mono text-red-900 break-all mb-1">{ctx.route}</div>
                  <div className="text-[11px] text-red-700/80">
                    Reported from the route above. Open it locally to reproduce.
                  </div>
                </div>
              )}

              {issue.screenshotKey && (
                <div className="bg-white border border-stone-200 rounded-xl shadow-sm p-4">
                  <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2 flex items-center justify-between">
                    <span>Screenshot</span>
                    {screenshotUrl && (
                      <a href={screenshotUrl} target="_blank" rel="noreferrer" className="text-admin-primary hover:underline">Open full size ↗</a>
                    )}
                  </div>
                  {screenshotUrl ? (
                    <a href={screenshotUrl} target="_blank" rel="noreferrer" className="block">
                      <img
                        src={screenshotUrl}
                        alt="Reported page screenshot"
                        className="w-full rounded-lg border border-stone-200 max-h-[500px] object-contain bg-stone-50"
                      />
                    </a>
                  ) : (
                    <RubyLoader variant="section" label="Loading screenshot…" />
                  )}
                </div>
              )}

              {issue.consoleErrors?.length > 0 && (
                <div className="bg-white border border-stone-200 rounded-xl shadow-sm p-4">
                  <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">
                    Console output ({issue.consoleErrors.length})
                  </div>
                  <pre className="bg-stone-900 text-red-200 rounded-lg p-3 text-[11px] font-mono overflow-x-auto leading-relaxed max-h-72">
{issue.consoleErrors.map((e: any) => `[${e.level}] ${e.message}${e.stack ? '\n' + e.stack : ''}`).join('\n')}
                  </pre>
                </div>
              )}

              {issue.networkFailures?.length > 0 && (
                <div className="bg-white border border-stone-200 rounded-xl shadow-sm p-4">
                  <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">
                    Failed network calls ({issue.networkFailures.length})
                  </div>
                  <div className="space-y-2">
                    {issue.networkFailures.map((n: any, i: number) => (
                      <div key={i} className="bg-stone-900 rounded-lg p-3 text-[11px] font-mono">
                        <div className="flex items-center gap-2 text-white">
                          <span className="text-red-300 font-semibold">{n.status || 'ERR'}</span>
                          <span className="text-stone-300">{n.method}</span>
                          <span className="text-stone-400 truncate">{n.url}</span>
                        </div>
                        {n.responseSnippet && (
                          <div className="text-stone-500 mt-1">Response: {n.responseSnippet}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {issue.pageState && (
                <div className="bg-white border border-stone-200 rounded-xl shadow-sm p-4">
                  <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Page state at report time</div>
                  <pre className="bg-stone-900 text-stone-200 rounded-lg p-3 text-[11px] font-mono overflow-x-auto max-h-60">{issue.pageState}</pre>
                </div>
              )}

              {aiPrompt && (
                <div ref={aiPromptRef} className="bg-white border border-red-300 rounded-xl shadow-sm p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs font-semibold text-red-700 uppercase tracking-wide">
                      AI prompt (paste into Claude Code)
                    </div>
                    <button
                      onClick={copyPrompt}
                      className="px-3 py-1.5 text-xs rounded-lg text-white font-medium inline-flex items-center gap-1.5"
                      style={{ background: RUBY }}
                    >
                      {copied ? <CheckCircle size={12} /> : <Copy size={12} />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="bg-stone-900 text-stone-100 border border-stone-700 rounded-lg p-3 text-[12px] font-mono whitespace-pre-wrap overflow-x-auto max-h-96">{aiPrompt.prompt}</pre>
                  {aiPrompt.screenshotUrl && (
                    <a
                      href={aiPrompt.screenshotUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-block text-xs text-admin-primary hover:underline"
                    >
                      Screenshot ↗
                    </a>
                  )}
                </div>
              )}

              {/* Comments */}
              <div className="bg-white border border-stone-200 rounded-xl shadow-sm p-4">
                <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">
                  Comments ({comments.length})
                </div>
                <div className="space-y-3 mb-3">
                  {comments.length === 0 && (
                    <div className="text-sm text-stone-500">No comments yet.</div>
                  )}
                  {comments.map((c) => (
                    <div key={c.id} className="flex gap-2">
                      <div className={`w-7 h-7 rounded-full text-white text-xs flex items-center justify-center font-semibold ${
                        c.authorRole === 'super_admin' ? 'bg-admin-primary' :
                        c.authorRole === 'system' ? 'bg-stone-400' : 'bg-blue-600'
                      }`}>
                        {(c.authorName || c.authorEmail || '?').slice(0, 1).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="text-xs">
                          <span className="text-stone-900 font-medium">{c.authorName || c.authorEmail}</span>{' '}
                          <span className="text-stone-500">· {c.authorRole.replace('_', ' ')} · {fmtTs(c.createdAt)}</span>
                        </div>
                        <div className="text-sm text-stone-700 mt-0.5 whitespace-pre-wrap">{c.body}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  rows={2}
                  placeholder="Reply to the reporter…"
                  className="w-full bg-white border border-stone-300 rounded-lg px-3 py-2 text-sm text-stone-900 placeholder-stone-400 focus:border-admin-primary focus:outline-none resize-none"
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={postComment}
                    disabled={!commentText.trim() || postingComment}
                    className="px-3 py-1.5 text-xs rounded-lg text-white font-medium disabled:opacity-50 inline-flex items-center gap-1.5"
                    style={{ background: RUBY }}
                  >
                    {postingComment && <RubyGemMark size={12} drawing color="currentColor" />}
                    Post
                  </button>
                </div>
              </div>

              {/* Context */}
              <div className="bg-white border border-stone-200 rounded-xl shadow-sm p-4">
                <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Context</div>
                <div className="grid grid-cols-2 gap-x-4">
                  <Field label="Reporter">{issue.reporterName || ctx.userName || '—'} <span className="text-stone-500">({issue.reporterEmail})</span></Field>
                  <Field label="Role">{ctx.userRole || 'unknown'}</Field>
                  <Field label="Tenant">
                    <div>{ctx.tenantName || '—'}</div>
                    <div className="text-[10px] text-stone-500 font-mono break-all">{issue.tenantId}</div>
                  </Field>
                  {(ctx.locationName || ctx.locationId) && (
                    <Field label="Location">
                      <div>{ctx.locationName || '—'}</div>
                      {ctx.locationId && (
                        <div className="text-[10px] text-stone-500 font-mono break-all">{ctx.locationId}</div>
                      )}
                    </Field>
                  )}
                  {ctx.registerId && (
                    <Field label="Register / Terminal"><span className="font-mono">{ctx.registerId}</span></Field>
                  )}
                  <Field label="Product">{ctx.productId || 'unknown'}</Field>
                  <Field label="Browser">{ctx.browser || '—'}</Field>
                  <Field label="OS">{ctx.os || '—'}</Field>
                  {ctx.viewport && <Field label="Viewport">{ctx.viewport.width} × {ctx.viewport.height}</Field>}
                  {ctx.appVersion && <Field label="App version">{ctx.appVersion}</Field>}
                  {ctx.bundleVersion && <Field label="Bundle">{ctx.bundleVersion}</Field>}
                  <Field label="Page title">{ctx.pageTitle || '—'}</Field>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation — custom UI per no-native-alerts rule */}
      {showDeleteConfirm && issue && (
        <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-5 border-b border-stone-200 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center">
                <Trash2 size={18} className="text-red-600" />
              </div>
              <div>
                <div className="text-base font-semibold text-stone-900">Delete this ticket?</div>
                <div className="text-xs text-stone-500">This can't be undone.</div>
              </div>
            </div>
            <div className="p-5 text-sm text-stone-700">
              <p className="mb-2">
                <span className="font-mono text-stone-500">#{issue.id?.slice(-8)}</span>{' '}
                — {issue.title}
              </p>
              <p className="text-stone-500">The issue, all its comments, and the screenshot will be permanently removed.</p>
            </div>
            <div className="p-4 bg-stone-50 border-t border-stone-200 flex justify-end gap-2 rounded-b-2xl">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-3 py-1.5 text-sm rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={deleteThisIssue}
                disabled={deleting}
                className="px-4 py-1.5 text-sm rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {deleting && <RubyGemMark size={14} drawing color="currentColor" />}
                Delete forever
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="text-xs mb-2">
    <div className="text-stone-500">{label}</div>
    <div className="text-stone-800">{children}</div>
  </div>
);

export default AppIssueDrawer;
